'use client';

import { createContext, useCallback, useContext, useRef } from 'react';

type EmptyPayload = NonNullable<unknown>;

/** Datensparsame Config-Metadaten des Selbstauskunft-Generators (§6.4). */
interface SelbstauskunftConfigPayload {
  preset: 'basis' | 'nach_besichtigung' | 'vor_vertragsabschluss' | 'custom';
  person_count: number;
  language: 'de' | 'en' | 'de-en';
  field_count: number;
}

// Base event types
export interface BaseAppEventTypes {
  'user.signedIn': { userId: string };
  // `email` (#592): erlaubt das Gaten von Meta-Pixel-SignupStart für interne
  // Test-/Mitarbeiter-Identitäten (optional, da nicht jeder Emitter sie kennt).
  'user.signedUp': { method: `magiclink` | `password`; email?: string };
  'user.updated': EmptyPayload;
  'checkout.started': { planId: string; account?: string };

  // Conversion-Event (#421): Umsatz-Abschluss (Checkout aktiviert + Mailbox)
  'checkout.completed': { rentalCaseId: string; account: string };

  // Mieterselbstauskunft-Generator (Tool-LP `/mieterselbstauskunft`,
  // Sanierungs-Spec §6.4). NIEMALS Formular-Inhalte/Absenderdaten/Feld-Keys in
  // den Payloads – nur Config-Metadaten: preset (UI-Shortcut oder 'custom'),
  // person_count, language, field_count (und `logged_in` beim Save-Klick).
  // Gemappt in analytics-provider.tsx auf die snake_case-PostHog-Namen;
  // Consent-Gating via CookieYes bleibt unangetastet.
  'selbstauskunft.toolOpened': EmptyPayload;
  'selbstauskunft.generated': SelbstauskunftConfigPayload;
  'selbstauskunft.pdfDownloaded': SelbstauskunftConfigPayload;
  'selbstauskunft.workspaceSaveClicked': SelbstauskunftConfigPayload & {
    logged_in: boolean;
  };

  // Add more base event types here
}

export type ConsumerProvidedEventTypes = EmptyPayload;

// Helper type for extending event types
export type ExtendedAppEventTypes<
  T extends ConsumerProvidedEventTypes = ConsumerProvidedEventTypes,
> = BaseAppEventTypes & T;

// Generic type for the entire module
export type AppEventType<T extends ConsumerProvidedEventTypes> =
  keyof ExtendedAppEventTypes<T>;

export type AppEvent<
  T extends ConsumerProvidedEventTypes = ConsumerProvidedEventTypes,
  K extends AppEventType<T> = AppEventType<T>,
> = {
  type: K;
  payload: ExtendedAppEventTypes<T>[K];
};

export type EventCallback<
  T extends ConsumerProvidedEventTypes,
  K extends AppEventType<T> = AppEventType<T>,
> = (event: AppEvent<T, K>) => void;

interface InternalAppEventsContextType<
  T extends ConsumerProvidedEventTypes = ConsumerProvidedEventTypes,
  K extends AppEventType<T> = AppEventType<T>,
> {
  emit: (event: AppEvent<never, never>) => void;
  on: (eventType: K, callback: EventCallback<T, K>) => void;
  off: (eventType: K, callback: EventCallback<T, K>) => void;
}

interface AppEventsContextType<T extends ConsumerProvidedEventTypes> {
  emit: <K extends AppEventType<T>>(event: AppEvent<T, K>) => void;

  on: <K extends AppEventType<T>>(
    eventType: K,
    callback: EventCallback<T, K>,
  ) => void;

  off: <K extends AppEventType<T>>(
    eventType: K,
    callback: EventCallback<T, K>,
  ) => void;
}

const AppEventsContext = createContext<InternalAppEventsContextType | null>(
  null,
);

export function AppEventsProvider<
  T extends ConsumerProvidedEventTypes = ConsumerProvidedEventTypes,
  K extends AppEventType<T> = AppEventType<T>,
>({ children }: React.PropsWithChildren) {
  const listeners = useRef<Record<K, EventCallback<T, K>[]>>(
    {} as Record<K, EventCallback<T, K>[]>,
  );

  const emit = useCallback(
    (event: AppEvent<T, K>) => {
      const eventListeners = listeners.current[event.type] ?? [];

      eventListeners.forEach((callback) => callback(event));
    },
    [listeners],
  );

  const on = useCallback((eventType: K, callback: EventCallback<T, K>) => {
    listeners.current = {
      ...listeners.current,
      [eventType]: [...(listeners.current[eventType] ?? []), callback],
    };
  }, []) as AppEventsContextType<T>['on'];

  const off = useCallback((eventType: K, callback: EventCallback<T, K>) => {
    listeners.current = {
      ...listeners.current,
      [eventType]: (listeners.current[eventType] ?? []).filter(
        (cb) => cb !== callback,
      ),
    };
  }, []) as AppEventsContextType<T>['off'];

  return (
    <AppEventsContext.Provider value={{ emit, on, off }}>
      {children}
    </AppEventsContext.Provider>
  );
}

export function useAppEvents<
  T extends ConsumerProvidedEventTypes = ConsumerProvidedEventTypes,
>(): AppEventsContextType<T> {
  const context = useContext(AppEventsContext);

  if (!context) {
    throw new Error('useAppEvents must be used within an AppEventsProvider');
  }

  return context as AppEventsContextType<T>;
}
